import React from 'react';
import { Box } from '@mui/material';

import { SidebarTabNames } from './constants';
import { SidebarTabNamesType } from './types';
import GeneralTab from './SidebarTabs/GeneralTab';
import OngoingCallsTab from './SidebarTabs/OngoingCallsTab';
import CallAnalysisTab from './SidebarTabs/CallAnalysisTab';
import type { CallAnalysisActions } from './SidebarTabs/analysis/CallAnalysisStructuredOutputSection';
import PostCallMessagesTab from './SidebarTabs/PostCallMessagesTab';
import AgentTab from './SidebarTabs/AgentTab';
import PhoneNumbersTab from './SidebarTabs/PhoneNumbersTab';
import AnalysisSummaryTab from './SidebarTabs/analysis/CallAnalysisStatistics';
import SettingsTab from './SidebarTabs/PostCallTab';
import SipTrunkTab from './SidebarTabs/SipTrunkTab';
import type { SipTrunkActions } from './SidebarTabs/SipTrunkTab';
import SquadsTab from './SidebarTabs/SquadsTab';
import { CampaignDto } from '@shared/services/apiService/apiService';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { AmbienceSettingsForm, AmbienceSettingsDraft } from '@features/Admin/AmbienceSettings';
import { AgentDto, BackgroundSoundResponseDto } from '@shared/services/apiService/apiService';
import {
  CampaignNoiseProcessingDraft,
  CampaignNoiseProcessingForm,
  CampaignNoiseProcessingValidation,
} from '@features/Admin/NoiseProcessing';
import {
  ConversationSettingsDraft,
  ConversationSettingsForm,
  ConversationSettingsValidation,
} from '@features/Admin/ConversationSettings';

interface EditCampaignTabContentProps {
  activeTab: SidebarTabNamesType;
  campaignData?: CampaignDto;
  campaignId?: string;
  companyName?: string;
  isLoading: boolean;
  isLoadingCampaignAgents: boolean;
  isSquadCampaign: boolean;
  isCompanyFetched: boolean;
  agents: AgentDto[];
  onAgentCreated: () => Promise<void>;
  isProductDetailsDirty: boolean;
  isSavingProductDetails: boolean;
  onProductDetailsSave: () => Promise<boolean>;
  onProductDetailsCancel: () => void;
  refetch: () => Promise<unknown>;
  refetchCampaignAgents: () => Promise<unknown>;
  responseLoadingFlag: boolean;
  setLeadListChanged: (changed: boolean) => void;
  customersRefreshKey: number;
  initialCustomersCount?: number;
  onCustomersCountChange?: (count: number) => void;
  initialCallsCount?: number;
  onCallsCountChange?: (count: number) => void;
  isLoadingBackgroundSounds: boolean;
  backgroundSounds: BackgroundSoundResponseDto[];
  ambienceSettingsDraft: AmbienceSettingsDraft | null;
  setAmbienceSettingsDraft: (draft: AmbienceSettingsDraft) => void;
  isAmbienceSettingsReadOnly: boolean;
  hasAttemptedAmbienceSettingsSave: boolean;
  generalSectionActions: React.ReactNode;
  phoneNumbersSectionActions: React.ReactNode;
  noiseProcessing: {
    draft: CampaignNoiseProcessingDraft | null;
    setDraft: (draft: CampaignNoiseProcessingDraft) => void;
    errors: CampaignNoiseProcessingValidation;
    isReadOnly: boolean;
  };
  conversationSettings: {
    draft: ConversationSettingsDraft | null;
    setDraft: (draft: ConversationSettingsDraft) => void;
    errors: ConversationSettingsValidation;
    isReadOnly: boolean;
  };
  callAnalysis: {
    onDirtyChange: (isDirty: boolean) => void;
    onActionsChange: (actions: CallAnalysisActions | null) => void;
  };
  sipTrunk: {
    onDirtyChange: (isDirty: boolean) => void;
    onActionsChange: (actions: SipTrunkActions | null) => void;
  };
  squads: {
    onDirtyChange: (isDirty: boolean) => void;
    onActionsChange: (
      actions: { save: () => Promise<boolean>; discard: () => void } | null,
    ) => void;
    onRequestAgentsTab: () => void;
  };
}

export const EditCampaignTabContent: React.FC<EditCampaignTabContentProps> = ({
  activeTab,
  campaignData,
  campaignId,
  companyName,
  isLoading,
  isLoadingCampaignAgents,
  isSquadCampaign,
  isCompanyFetched,
  agents,
  onAgentCreated,
  isProductDetailsDirty,
  isSavingProductDetails,
  onProductDetailsSave,
  onProductDetailsCancel,
  refetch,
  refetchCampaignAgents,
  responseLoadingFlag,
  setLeadListChanged,
  customersRefreshKey,
  initialCustomersCount,
  onCustomersCountChange,
  initialCallsCount,
  onCallsCountChange,
  isLoadingBackgroundSounds,
  backgroundSounds,
  ambienceSettingsDraft,
  setAmbienceSettingsDraft,
  isAmbienceSettingsReadOnly,
  hasAttemptedAmbienceSettingsSave,
  generalSectionActions,
  phoneNumbersSectionActions,
  noiseProcessing,
  conversationSettings,
  callAnalysis,
  sipTrunk,
  squads,
}) => {
  switch (activeTab) {
    case SidebarTabNames.GENERAL:
      return <GeneralTab campaign={campaignData} actions={generalSectionActions} />;
    case SidebarTabNames.AGENT:
      return (
        <AgentTab
          isLoading={isLoading || isLoadingCampaignAgents}
          onAgentCreated={onAgentCreated}
          isProductDetailsDirty={isProductDetailsDirty}
          isSavingProductDetails={isSavingProductDetails}
          onProductDetailsSave={onProductDetailsSave}
          onProductDetailsCancel={onProductDetailsCancel}
          refetch={async () => {
            await refetch();
            if (!isSquadCampaign) {
              await refetchCampaignAgents();
            }
          }}
          agents={agents}
        />
      );
    case SidebarTabNames.SQUADS:
      return (
        <SquadsTab
          isLoading={isLoading || isLoadingCampaignAgents}
          campaign={campaignData}
          agents={agents}
          refetch={refetch}
          onDirtyChange={squads.onDirtyChange}
          onActionsChange={squads.onActionsChange}
          onRequestAgentsTab={squads.onRequestAgentsTab}
        />
      );
    case SidebarTabNames.PHONE_NUMBERS:
      return campaignData?.type === CampaignTypeEnum.OUTBOUND ? (
        <PhoneNumbersTab
          campaign={campaignData}
          refetch={refetch}
          responseLoadingFlag={responseLoadingFlag}
          setLeadListChanged={setLeadListChanged}
          customersRefreshKey={customersRefreshKey}
          initialCustomersCount={initialCustomersCount}
          onCustomersCountChange={onCustomersCountChange}
          actions={phoneNumbersSectionActions}
        />
      ) : null;
    case 'analysisSummary':
      return campaignData ? <AnalysisSummaryTab campaign={campaignData} /> : null;
    case SidebarTabNames.SETTINGS:
      return campaignData ? (
        <SettingsTab campaignId={campaignData.id} isCampaignActive={campaignData.status} />
      ) : null;
    case SidebarTabNames.SIP_TRUNK:
      return isCompanyFetched && campaignData ? (
        <SipTrunkTab
          campaign={campaignData}
          onDirtyChange={sipTrunk.onDirtyChange}
          onActionsChange={sipTrunk.onActionsChange}
        />
      ) : null;
    case SidebarTabNames.AMBIENCE_SETTINGS:
      return campaignData ? (
        <Box sx={{ width: '760px', maxWidth: '100%' }}>
          <AmbienceSettingsForm
            isLoadingSounds={isLoadingBackgroundSounds}
            sounds={backgroundSounds}
            value={ambienceSettingsDraft}
            onChange={setAmbienceSettingsDraft}
            ambientDisabled={isAmbienceSettingsReadOnly}
            thinkingDisabled={isAmbienceSettingsReadOnly}
            showOverrideControls
            hasAttemptedSave={hasAttemptedAmbienceSettingsSave}
          />
        </Box>
      ) : null;
    case SidebarTabNames.NOISE_PROCESSING:
      return campaignData ? (
        <Box sx={{ width: '760px', maxWidth: '100%' }}>
          <CampaignNoiseProcessingForm
            value={noiseProcessing.draft}
            onChange={noiseProcessing.setDraft}
            errors={noiseProcessing.errors}
            disabled={noiseProcessing.isReadOnly}
          />
        </Box>
      ) : null;
    case SidebarTabNames.CONVERSATION_SETTINGS:
      return campaignData ? (
        <ConversationSettingsForm
          value={conversationSettings.draft}
          onChange={conversationSettings.setDraft}
          errors={conversationSettings.errors}
          disabled={conversationSettings.isReadOnly}
        />
      ) : null;
    case SidebarTabNames.ONGOING_CALLS:
      return campaignData && campaignId ? (
        <OngoingCallsTab
          campaignId={campaignId}
          campaignData={campaignData}
          campaignType={campaignData.type}
          initialCallsCount={initialCallsCount}
          onCallsCountChange={onCallsCountChange}
        />
      ) : null;
    case SidebarTabNames.CALL_ANALYSIS:
      return campaignData ? (
        <CallAnalysisTab
          campaign={campaignData}
          onDirtyChange={callAnalysis.onDirtyChange}
          onActionsChange={callAnalysis.onActionsChange}
        />
      ) : null;
    case SidebarTabNames.POST_CALL_MESSAGES:
      return campaignData ? (
        <PostCallMessagesTab
          campaignId={campaignData.id}
          campaignName={campaignData.title ?? ''}
          companyName={companyName ?? ''}
        />
      ) : null;
    case SidebarTabNames.STATISTICS:
      return campaignData ? <AnalysisSummaryTab campaign={campaignData} /> : null;
    default:
      return null;
  }
};
